import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Loader2, Pencil, Plus, Sparkles, Trash2, Trophy, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { brl, fmtDate, pct } from "@/lib/format";
import PhotoCapture, { AuthImage, PhotoViewer } from "@/components/conference/PhotoCapture";
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Loading, Page, PageHeader } from "@/components/kit";

const EMPTY = { name: "", cnpj: "", phone: "", email: "", notes: "", photos: [] };

export default function Suppliers() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [viewing, setViewing] = useState(null);
  const records = useQuery({ queryKey: ["supplier-records"], queryFn: () => api("/suppliers") });
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["suppliers"], queryFn: () => api("/reports/suppliers") });

  const invalidate = () => ["supplier-records", "suppliers"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const save = useMutation({
    mutationFn: () => (editing === "new" ? api("/suppliers", { method: "POST", body: form }) : api(`/suppliers/${editing}`, { method: "PUT", body: form })),
    onSuccess: () => { invalidate(); toast.success("Fornecedor salvo"); setEditing(null); },
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (id) => api(`/suppliers/${id}`, { method: "DELETE" }),
    onSuccess: () => { invalidate(); toast.success("Fornecedor excluído"); },
    onError: (e) => toast.error(e.message),
  });

  const startNew = () => { setForm(EMPTY); setEditing("new"); };
  const startEdit = (s) => {
    setForm({ name: s.name || "", cnpj: s.cnpj || "", phone: s.phone || "", email: s.email || "", notes: s.notes || "", photos: s.photos || [] });
    setEditing(s.id);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [readingAi, setReadingAi] = useState(false);
  const readWithAi = async () => {
    setReadingAi(true);
    try {
      const r = await api("/suppliers/read-photo", { method: "POST", body: { photo_path: form.photos[form.photos.length - 1] } });
      setForm((f) => ({ ...f, name: r.name || f.name, cnpj: r.cnpj || f.cnpj, phone: r.phone || f.phone, email: r.email || f.email }));
      toast.success("Dados da nota preenchidos. Confira antes de salvar.");
    } catch (e) {
      toast.error(e.message || "Falha na leitura da nota");
    } finally {
      setReadingAi(false);
    }
  };
  const addPhoto = (path) => setForm((f) => ({ ...f, photos: [...f.photos, path] }));
  const removePhoto = (path) => setForm((f) => ({ ...f, photos: f.photos.filter((p) => p !== path) }));
  const remove = (s) => { if (window.confirm(`Excluir o fornecedor ${s.name}?`)) del.mutate(s.id); };

  return (
    <Page>
      <PageHeader title="Fornecedores" back subtitle="Cadastre os fornecedores e anexe fotos das notas fiscais." right={!editing && <Button icon={Plus} onClick={startNew} data-testid="supplier-new-button">Novo fornecedor</Button>} />

      {editing && (
        <Card className="mb-6" data-testid="supplier-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Nome do fornecedor"><Input value={form.name} onChange={set("name")} data-testid="supplier-name-input" /></Field>
            <Field label="CNPJ"><Input value={form.cnpj} onChange={set("cnpj")} data-testid="supplier-cnpj-input" /></Field>
            <Field label="Telefone"><Input value={form.phone} onChange={set("phone")} data-testid="supplier-phone-input" /></Field>
            <Field label="E-mail"><Input type="email" value={form.email} onChange={set("email")} data-testid="supplier-email-input" /></Field>
          </div>
          <div className="mt-4">
            <Field label="Observações"><textarea className="input" rows={2} value={form.notes} onChange={set("notes")} data-testid="supplier-notes-input" /></Field>
          </div>
          <div className="mt-5 flex flex-col gap-3">
            <span className="label">Fotos da nota fiscal</span>
            <PhotoCapture onUploaded={addPhoto} allowPdf />
            {form.photos.length > 0 && (
              <Button type="button" variant="secondary" icon={readingAi ? Loader2 : Sparkles} disabled={readingAi} onClick={readWithAi} data-testid="supplier-ai-read-button">
                {readingAi ? "Lendo a nota..." : "Ler nota com IA e preencher"}
              </Button>
            )}
            {form.photos.length > 0 && (
              <div className="flex flex-wrap gap-3" data-testid="supplier-photos">
                {form.photos.map((p) => (
                  <div key={p} className="relative w-28 h-28">
                    {p.toLowerCase().endsWith(".pdf") ? (
                      <a href={fileUrl(p)} target="_blank" rel="noreferrer" className="w-28 h-28 rounded-lg flex flex-col items-center justify-center gap-1 caption" style={{ background: "var(--surface-alt)" }} data-testid={`supplier-photo-${p}`}><FileText size={28} />PDF</a>
                    ) : (
                    <AuthImage path={p} className="w-28 h-28 rounded-lg cursor-pointer" onClick={() => setViewing(p)} testid={`supplier-photo-${p}`} />
                    )}
                    <button type="button" className="icon-btn absolute top-1 right-1" onClick={() => removePhoto(p)} title="Remover foto" data-testid={`remove-supplier-photo-${p}`}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="mt-5 flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setEditing(null)} data-testid="supplier-cancel-button">Cancelar</Button>
            <Button loading={save.isPending} disabled={!form.name.trim()} onClick={() => save.mutate()} data-testid="supplier-save-button">Salvar</Button>
          </div>
        </Card>
      )}

      {records.isLoading ? <Loading /> : !records.data?.length ? (
        <EmptyState title="Nenhum fornecedor cadastrado" text="Use Novo fornecedor para cadastrar e anexar fotos das notas." testid="suppliers-empty" />
      ) : (
        <div className="card divide mb-8" style={{ padding: 0 }} data-testid="supplier-records">
          {records.data.map((s) => (
            <div key={s.id} className="flex items-center gap-3" style={{ padding: "12px var(--pad)" }} data-testid={`supplier-record-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="h3 truncate">{s.name}</div>
                <div className="caption truncate">{[s.cnpj, s.phone, s.email].filter(Boolean).join(" · ") || "Sem dados de contato"}{s.photos?.length ? ` · ${s.photos.length} foto(s)` : ""}</div>
              </div>
              <button className="icon-btn" onClick={() => startEdit(s)} data-testid={`edit-supplier-${s.id}`}><Pencil size={15} /></button>
              <button className="icon-btn" onClick={() => remove(s)} data-testid={`delete-supplier-${s.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      <h2 className="h3 mb-3">Ranking de preços</h2>
      <p className="caption mb-4">Preço médio por produto em todas as notas com itens lançadas. O primeiro é o mais barato.</p>
      {isLoading ? <Loading /> : isError ? <ErrorState message={error.message} onRetry={refetch} /> : !data.length ? (
        <EmptyState title="Sem dados ainda" text="Lance notas fiscais com itens para comparar os preços dos fornecedores." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="suppliers-list">
          {data.map((p) => (
            <div key={p.product_id} className="card" data-testid={`supplier-product-${p.product_id}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="h3 flex-1 truncate">{p.product_name}</span>
                {p.saving_pct > 0 && <Badge tone="success">até {pct(p.saving_pct, 0)} mais barato</Badge>}
              </div>
              <div className="flex flex-col gap-2">
                {p.suppliers.map((s, i) => (
                  <div key={s.supplier} className="flex items-center gap-3" data-testid={`supplier-row-${p.product_id}-${i}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: i === 0 ? "var(--brand)" : "var(--surface-alt)", color: i === 0 ? "#fff" : "var(--muted)" }}>
                      {i === 0 ? <Trophy size={14} /> : i + 1}
                    </div>
                    <div className="flex-1 min-w-0"><div className="label truncate">{s.supplier}</div><div className="caption">{s.count} nota(s) · última {fmtDate(s.last_date)}</div></div>
                    <div className="text-right"><div className="label tnum">{brl(s.avg)}/{p.unit}</div><div className="caption tnum">mín. {brl(s.min)}</div></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {viewing && <PhotoViewer path={viewing} onClose={() => setViewing(null)} />}
    </Page>
  );
}
